import React, { useState, useEffect } from 'react';
import RegionalManagerSelect from '../components/RegionalManagerSelect';
import GroupManager from '../components/GroupManager';
import DeleteDocButton from '../components/DeleteDocButton';
import { IconUpload, IconDoc, IconFolder, IconChart, IconBuilding } from '../components/Icons';
import './AdminUpload.css';

// Seção de arquivos dos grupos (docs-admcen + docs-gerenciais): o compartilhador
// vê e pode excluir os documentos compartilhados nos grupos (CR FASE 3, item 3).
function GroupDocsSection({ reloadKey }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/listGroupDocs');
      const data = await res.json();
      if (data.error) { setError(data.error); setFiles([]); }
      else { setError(null); setFiles(data.files || []); }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const handleDeleted = (fullPath, container) => {
    setFiles((prev) => prev.filter((f) => !(f.fullPath === fullPath && f.container === container)));
  };

  const labelContainer = (c) =>
    c === 'docs-admcen' ? 'ADMCEN (Corporativo)'
      : c === 'docs-gerenciais' ? 'Gerenciais (Regional)'
        : c === 'docs' ? 'Pessoal (individual)'
          : c;

  return (
    <div className="upload-form" style={{ marginTop: 24 }}>
      <h3 className="section-title-admin"><span className="section-icon"><IconDoc /></span>Arquivos dos grupos</h3>
      <p className="section-description">
        Documentos compartilhados nos grupos (ADMCEN e Gerenciais por regional) e os que você
        compartilhou individualmente por e-mail. Você pode excluí-los aqui.
      </p>

      {loading ? (
        <p className="empty-admins">Carregando arquivos...</p>
      ) : error ? (
        <div className="upload-result error"><p>{error}</p></div>
      ) : files.length === 0 ? (
        <p className="empty-admins">Nenhum arquivo nos grupos.</p>
      ) : (
        <div className="admins-list">
          <label>Arquivos ({files.length})</label>
          <ul>
            {files.map((f, i) => (
              <li key={i} className="admin-item">
                <div>
                  <span className="admin-email" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <IconDoc size={15} /> {f.name}
                  </span>
                  <span className="admin-meta">
                    {labelContainer(f.container)}{f.location ? ` • ${f.location}` : ''}
                    {f.mine ? ' • enviado por mim' : (f.uploadedBy ? ` • por ${f.uploadedBy}` : '')}
                  </span>
                </div>
                <DeleteDocButton
                  canDelete={true}
                  container={f.container}
                  file={f.fullPath}
                  onDeleted={() => handleDeleted(f.fullPath, f.container)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UploadDocs({ user }) {
  const [container, setContainer] = useState('docs-publicos');
  const [targetPath, setTargetPath] = useState('');
  const [selectedManager, setSelectedManager] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [docsReload, setDocsReload] = useState(0);

  const containerOptions = [
    { value: 'docs-publicos', label: 'Documentos Públicos', type: 'flat' },
    { value: 'docs-diretoria', label: 'Diretoria', type: 'flat' },
    { value: 'docs-admcen', label: 'ADMCEN (Corporativo)', type: 'flat' },
    { value: 'docs-gerenciais', label: 'Gerenciais (por regional)', type: 'regional' },
    { value: 'docs', label: 'Pessoal (por email)', type: 'byEmail' }
  ];

  const currentOption = containerOptions.find(o => o.value === container);

  // Reset campos ao trocar container
  useEffect(() => {
    setTargetPath('');
    setSelectedManager(null);
  }, [container]);

  const handleUpload = async () => {
    if (!file) {
      setResult({ error: 'Selecione um arquivo' });
      return;
    }

    // Validação por tipo
    if (currentOption.type === 'regional' && !selectedManager) {
      setResult({ error: 'Selecione um gerente regional' });
      return;
    }

    if (currentOption.type === 'byEmail' && !targetPath) {
      setResult({ error: 'Informe o email do usuário' });
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const params = new URLSearchParams({
        container,
        fileName: file.name
      });

      if (currentOption.type === 'regional') {
        params.append('path', selectedManager.officeLocation);
      } else if (currentOption.type === 'byEmail') {
        params.append('path', targetPath);
      }

      const arrayBuffer = await file.arrayBuffer();

      const response = await fetch(`/api/uploadDocument?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-File-Name': file.name,
          'X-File-Type': file.type || 'application/octet-stream'
        },
        body: arrayBuffer
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        setResult({
          error: data.error || 'Erro desconhecido',
          details: data.details || data.reason,
          status: response.status
        });
      } else {
        setResult(data);
        setFile(null);
        setSelectedManager(null);
        setTargetPath('');
        document.getElementById('file-input').value = '';
        // Atualiza a lista de arquivos dos grupos (caso o envio tenha sido p/ um grupo).
        setDocsReload((k) => k + 1);
      }
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2><span className="section-icon"><IconUpload /></span>Enviar Documentos</h2>
        <p>Upload de documentos para os containers do portal</p>
      </div>

      <div className="upload-form">
        <h3 className="section-title-admin"><span className="section-icon"><IconUpload /></span>Upload de Documentos</h3>

        <div className="form-group">
          <label>Container de destino</label>
          <select value={container} onChange={(e) => setContainer(e.target.value)}>
            {containerOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Campo para regional (dropdown de gerentes) */}
        {currentOption.type === 'regional' && (
          <div className="form-group">
            <label>Gerente Regional</label>
            <RegionalManagerSelect
              value={selectedManager}
              onChange={setSelectedManager}
            />
            {selectedManager && (
              <p className="path-preview">
                <IconFolder size={14} /> Será salvo em: <code>{selectedManager.officeLocation}/</code>
              </p>
            )}
          </div>
        )}

        {/* Campo para email (docs pessoais) */}
        {currentOption.type === 'byEmail' && (
          <div className="form-group">
            <label>Email do usuário</label>
            <input
              type="email"
              value={targetPath}
              onChange={(e) => setTargetPath(e.target.value)}
              placeholder="email@cvale.com.br"
            />
          </div>
        )}

        <div className="form-group">
          <label>Arquivo</label>
          <input id="file-input" type="file" onChange={(e) => setFile(e.target.files[0])} />
          {file && <p className="file-selected"><IconDoc size={14} /> {file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
        </div>

        <button className="upload-button" onClick={handleUpload} disabled={uploading || !file}>
          {uploading ? 'Enviando...' : 'Fazer Upload'}
        </button>

        {result && (
          <div className={`upload-result ${result.success ? 'success' : 'error'}`}>
            {result.success ? (
              <>
                <strong>✅ Upload realizado!</strong>
                <p>Container: <code>{result.container}</code></p>
                <p>Caminho: <code>{result.path}</code></p>
              </>
            ) : (
              <>
                <strong>❌ Erro:</strong>
                <p>{result.error}</p>
                {result.details && <p className="error-details">{result.details}</p>}
              </>
            )}
          </div>
        )}
      </div>

      {/* Gestão dos grupos (compartilhador também gerencia) — CR FASE 3, itens 2 e 3 */}
      <GroupManager
        title="Grupo Regional"
        icon={<IconChart />}
        description="Acesso aos documentos gerenciais da regional. Entra por cargo (gerente regional e comerciais lotados em regional) — inclua manualmente para cobrir exceções/férias."
        group="regional"
      />

      <GroupManager
        title="Grupo ADMCEN (Corporativo)"
        icon={<IconBuilding />}
        description="Acesso aos documentos corporativos. Entra por cargo (diretoria, gerentes de departamento e comerciais lotados em Compras) — inclua manualmente para cobrir exceções."
        group="admcen"
      />

      <GroupDocsSection reloadKey={docsReload} />
    </div>
  );
}

export default UploadDocs;
